<template>
    <div class="muven-container">
        <div class="muven-router-name-container">
            <p class="muven-router-name">Formas de pagamento</p>
        </div>

        <div class="muven-content">
            <Loading page v-if="loading" />        

            <div class="mb-3" v-if="!loading">
                <div>
                    <button type="button" 
                        class="btn btn-primary"
                        @click="showFilterModal()">
                        Filtro
                    </button>
                </div>
            </div>

            <FilterInformationCmp 
                v-if="!loading"
                :filter-submitted="paymentFilterSubmitted"
                :filter-properties="paymentFilterProperties" />

            <div class="modal" tabindex="-1" role="dialog" id="muven-payment-list-filter-modal">
                <div class="modal-dialog" role="document">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title">Filtro de marcas</h5>
                        </div>
                        <div class="modal-body">
                            <div class="row">
                                <div class="col-12 form-group">
                                    <label>Tipo de filtro</label>
                                    <div class="btn-group">
                                       <button v-for="(filterOption, indexFilterOption) in filterOptions" :key="indexFilterOption"
                                        type="button" 
                                        class="btn btn-outline-secondary"
                                        :class="{active: filterOption.active}"
                                        @click="updateFilterOption(filterOption)">{{filterOption.description}}</button>
                                    </div>
                                </div>
                            </div>

                            <div class="row" v-if="isFilterOptionByBackendInfo">
                                <div class="col-12 form-group">
                                    <label>Id no canal</label>
                                    <input type="text" 
                                        class="form-control" 
                                        v-model="paymentFilter.idExternalChannel" />
                                </div>
                            </div>

                            <div class="row" v-if="isFilterOptionByChannelInfo">
                                <div class="col-12 form-group">
                                    <label>Id na retaguarda</label>
                                    <input type="text" 
                                        class="form-control" 
                                        v-model="paymentFilter.idSource" />
                                </div>
                            </div>

                            <div class="row" v-if="isFilterOptionAdvanced">
                                <div class="col-12 form-group">
                                    <label>Nome</label>
                                    <input type="text" 
                                        class="form-control" 
                                        v-model="paymentFilter.name" />
                                </div>

                                <div class="col-12 form-group">
                                    <label>Exportação</label>
                                        <multiselect
                                            v-model="paymentFilter.allowExport"
                                            :options="exportableList"
                                            :multiple="false"
                                            :show-labels="false"
                                            label="description"
                                            track-by="bool"
                                            placeholder="" />
                                </div>
                            </div>
                        </div>
                        <div class="modal-footer">
                            <button type="button" 
                                class="btn btn-primary" 
                                @click="getPaymentsWithFilter(paymentFilter)"
                                :disabled="loadingFilter">
                                    Filtrar
                                    <Loading v-show="loadingFilter" />
                            </button>

                            <button type="button" 
                                class="btn btn-outline-secondary" 
                                @click="clearFilter()">Restaurar</button>

                            <button type="button" 
                                class="btn btn-outline-secondary" 
                                @click="closeFilterModal()">Fechar</button>
                        </div>
                    </div>
                </div>
            </div>

            <NothingFound v-if="nothingToShow" />

            <div v-if="!loading && !nothingToShow">
                <div class="table-responsive">
                    <table class="table table-sm table-hover table-striped">
                        <thead>
                            <tr>
                                <th>Nome</th>
                                <th class="status-col">Status</th>
                                <th></th>
                            </tr>
                        </thead>

                        <tbody>
                            <tr v-for="(payment, index) in paymentList" :key="index">
                                <td>{{payment.name | infoOrPlaceholder}}</td>
                                <td>
                                    <span v-if="payment.isActive">
                                        <span class="muven-td-icon" >
                                            <fa icon="check-circle" 
                                                class="text-success" />
                                        </span>
                                    
                                        <span>Ativa</span>
                                    </span>

                                    <span v-if="!payment.isActive">
                                        <span class="muven-td-icon">
                                            <fa icon="ban"                                             
                                                class="text-muted" />
                                        </span>

                                        <span>Inativa</span>
                                    </span>
                                </td>
                                <td class="text-center muven-td-with-buttons">
                                    <button type="button" 
                                        class="btn btn-default text-muted" 
                                        data-toggle="dropdown" >
                                        <fa icon="ellipsis-v" />
                                    </button>

                                    <div class="dropdown-menu dropdown-menu-right muven-action-menu">
                                        <router-link class="dropdown-item"
                                            :to="detailUrl(payment)">
                                            <fa icon="file-alt" fixed-width class="muven-action-icon" />
                                            <span class="muven-action-label">Detalhes</span>
                                        </router-link>

                                        <p class="dropdown-divider"></p>

                                        <a class="dropdown-item"
                                            :href="getAuditUrl(payment.id, EntityEnum.Payment)"
                                            target="_blank"
                                            rel="noopener"
                                            title="Abre em uma nova aba">
                                            <fa icon="history" fixed-width class="muven-action-icon" />
                                            <span class="muven-action-label">Ir para auditoria</span>
                                            <fa icon="external-link-alt" class="muven-action-external" />
                                        </a>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <ListCount :amount="paymentList.length" />
                </div>

                <div v-if="showPaginationButton" class="text-center">
                    <button class="btn btn-outline-secondary"
                        @click="getMorePayments()"
                        :disabled="loadingNextPage">
                        <span v-show="!loadingNextPage">Mais resultados</span>
                        <fa v-show="loadingNextPage" icon="sync-alt" spin/>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {cloneDeep} from 'lodash'
import paymentService from '@/payment/services/PaymentService'
import { PaymentListFilterModel } from '@/payment/models/PaymentListFilterModel'
import { PaginationModel } from '@/utils/models/PaginationModel'
import modalService from '@/utils/services/ModalService'
import { dateTimeFilter } from '@/utils/filters/DateTimeFilter'
import FilterInformationCmp from '@/utils/components/FilterInformationCmp'
import { EntityEnum } from '@c2b/muven-commons'
import GetAuditUrl from '@/utils/mixins/GetAuditUrl'
import { infoOrPlaceholder } from '@/utils/filters/InformationOrPlaceholderFilter'

const ID_FILTER_MODAL = '#muven-payment-list-filter-modal'
const FILTER_BY_BACKEND_INFO = 'filter_by_backend_info'
const FILTER_BY_CHANNEL_INFO = 'filter_by_channel_info'
const FILTER_ADVANCED = 'filter_advanced'

export default {
    name: 'PaymentListView',
    data() {
        return {
            EntityEnum,
            loading: true,
            loadingFilter: false,
            loadingNextPage: false,
            showPaginationButton: true,
            pagination: new PaginationModel(),
            paymentFilter: new PaymentListFilterModel(),
            paymentFilterSubmitted: new PaymentListFilterModel(),
            paymentList: [],
            filterOptions: [
                {
                    enum: FILTER_ADVANCED,
                    description: 'Geral',
                    active: true,
                },
                {
                    enum: FILTER_BY_BACKEND_INFO,
                    description: 'Retaguarda',
                    active: false,
                },
                {
                    enum: FILTER_BY_CHANNEL_INFO,
                    description: 'Canal',
                    active: false,
                },
            ],
            filterOptionSelected: {
                enum: FILTER_ADVANCED,
                description: 'Geral',
                active: true,
            },
            exportableList: [
                {
                    bool: true,
                    description: 'Apenas formas de pagamento que SÃO EXPORTADAS'
                },
                {
                    bool: false,
                    description: 'Apenas formas de pagamento que NÃO SÃO EXPORTADAS'
                },
            ],
            paymentFilterProperties: [
                {
                    type: 'string',
                    label: 'Nome',
                    property: 'name',
                },
                {
                    type: 'string',
                    label: 'Id na retaguarda',
                    property: 'idSource',
                },
                {
                    type: 'string',
                    label: 'Id no canal',
                    property: 'idExternalChannel',
                },
                {
                    type: 'booleanwrapped',
                    label: 'Exportação',
                    property: 'allowExport',
                },
            ],
        }
    },
    mixins: [
        GetAuditUrl,
    ],
    components: {
        FilterInformationCmp,
    },
    filters: {
        dateTimeFilter,
        infoOrPlaceholder,
    },
    methods: {
        async getMorePayments() {
            try {
                this.loadingNextPage = true
                const newPaymentList = await paymentService.getPayments(this.pagination.actual(), this.paymentFilter)
                this.paymentList.push(...newPaymentList)

                if (!newPaymentList.length) {
                    this.showPaginationButton = false
                }

                this.pagination.next()
            } catch(e) {
                this.$floatingAlert.error({
                    message: e.message,
                })
            }

            this.loadingNextPage = false
        },
        async getPaymentsWithFilter(filterList) {
            this.showPaginationButton = true
            this.paymentFilterSubmitted = cloneDeep(filterList)
            this.pagination.reset()
            this.loadingFilter = true
            try {
                this.paymentList = await paymentService.getPayments(this.pagination.actual(), filterList)
                this.closeFilterModal()
                this.pagination.next()
            } catch (e) {
                this.$floatingAlert.error({
                    message: e.message,
                })
            }

            this.loadingFilter = false
        },
        updateFilterOption(filterOption) {
            this.filterOptions.forEach((fo) => {
                fo.active = false
            })

            this.filterOptionSelected = Object.assign(filterOption, {active: true})            
            this.clearFilter()
        },
        goto(payment) {
            this.$router.push(`marcas/${payment.id}`)
        },
        async showFilterModal() {
            await modalService.show(ID_FILTER_MODAL)
        },
        async closeFilterModal() {
            await modalService.hide(ID_FILTER_MODAL)
        },
        clearFilter() {
            this.paymentFilter = new PaymentListFilterModel()
        },
    },
    async mounted() {
        try {
            this.paymentList = await paymentService.getPayments(this.pagination.actual(), this.paymentFilter)
            this.pagination.next()
        } catch (err) {
            this.$floatingAlert.error({
                message: err.message,
            })
        }

        this.loading = false
    },
    computed: {
        nothingToShow() {
            return !this.loading && !this.paymentList.length
        },     
        isFilterOptionByBackendInfo() {
            return this.filterOptionSelected.enum === FILTER_BY_BACKEND_INFO
        },
        isFilterOptionByChannelInfo() {
            return this.filterOptionSelected.enum === FILTER_BY_CHANNEL_INFO
        },
        isFilterOptionAdvanced() {
            return this.filterOptionSelected.enum === FILTER_ADVANCED
        },
        detailUrl() {
            return (payment) => {
                return `/formas_pagamento/${payment.id}`
            }
        },
    },
}
</script>

<style lang="scss" scoped>

</style>
